/**
 * Planejamento Financeiro (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_planejamento.py).
 *
 * Todas as entradas ficam na URL (link compartilhável de uma simulação). A consulta
 * espera 400 ms sem mudanças antes de ir à API, para não simular a cada tecla.
 */
import { Alert, NumberInput, Select, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { AvisosApi } from '../../components/AvisosApi';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import {
  BarraFiltros,
  CampoData,
  CampoLeitura,
  CampoOpcoes,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';
interface DadosPlanejamento {
  variavel: string;
  idade_atual: number;
  expectativa_vida: number;
  periodo_distribuicao: number;
  expectativa_minima: number;
  opcoes_meta: string[];
  erros: string[];
  meta: { rotulo: string; valor: string } | { erro: string } | null;
  cartoes: { rotulo: string; valor: string }[][];
  avisos: { tipo: 'aviso' | 'info'; texto: string }[];
  kpis: KpiApi[];
  graficos: { evolucao: GraficoApi; contribuicoes: GraficoApi };
  tabelas: { anual: TabelaApi; mensal: TabelaApi };
}
export const PADROES = {
  modo: 'simulacao',
  nascimento: '1980-01-02',
  metodo: 'expectativa',
  patrimonio: 100000,
  acumulacao: 20,
  aporte: 5000,
  expectativa: 85,
  distribuicao: 10,
  resgate: 10000,
  rentabilidade: 10,
  inflacao: 4,
  aliquota: 15,
  alvo: 1000000,
  variavel: '',
};
const ROTULOS_META: Record<string, string> = {
  aporte: 'Aporte Mensal',
  resgate: 'Resgate Mensal',
  rentabilidade: 'Rentabilidade Nominal Esperada',
  patrimonio_inicial: 'Patrimônio Inicial',
};
const REAIS = { thousandSeparator: '.', decimalSeparator: ',', prefix: 'R$ ' } as const;
const PERCENTUAL = { decimalSeparator: ',', decimalScale: 1, step: 0.1, suffix: '%' } as const;
function Cartao({ itens }: { itens: { rotulo: string; valor: string }[] }) {
  return (
    <Alert color="blue" variant="light">
      <Stack gap={2}>
        {itens.map((item) => (
          <Text key={item.rotulo} size="sm">
            <b>{item.rotulo}:</b> {item.valor}
          </Text>
        ))}
      </Stack>
    </Alert>
  );
}
export function PlanejamentoFinanceiro() {
  const [filtros, definir] = useFiltrosUrl(PADROES);
  const [consultados] = useDebouncedValue(filtros, 400);
  const meta = filtros.modo === 'meta';
  const consulta = useQuery({
    queryKey: ['carteiras.planejamento', consultados],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosPlanejamento>>(
        '/carteiras-administradas/planejamento-financeiro',
        { ...consultados, variavel: consultados.variavel || undefined },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const dados = consulta.data?.data;
  /** NumberInput que só grava na URL valores válidos (dentro de [min, max]). */
  const numero = (
    campo: keyof typeof PADROES,
    rotulo: string,
    extra: Partial<React.ComponentProps<typeof NumberInput>> & { min?: number; max?: number } = {},
  ) => (
    <NumberInput
      label={rotulo}
      value={filtros[campo] as number}
      onChange={(v) => {
        const min = extra.min ?? 0;
        const max = extra.max ?? Infinity;
        if (typeof v === 'number' && v >= min && v <= max) definir({ [campo]: v });
      }}
      min={0}
      {...extra}
    />
  );
  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Planejamento Financeiro"
        descricao="Simulador de acumulação e distribuição de patrimônio, com cálculo da variável necessária para atingir uma meta."
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Planejamento">
          <CampoOpcoes
            rotulo="Modo"
            valor={filtros.modo}
            aoMudar={(modo) => definir({ modo })}
            opcoes={[
              { valor: 'simulacao', rotulo: 'Simulação' },
              { valor: 'meta', rotulo: 'Meta' },
            ]}
          />
          <CampoOpcoes
            rotulo="Método"
            valor={filtros.metodo}
            aoMudar={(metodo) => definir({ metodo })}
            opcoes={[
              { valor: 'expectativa', rotulo: 'Expectativa de vida' },
              { valor: 'distribuicao', rotulo: 'Período de distribuição' },
            ]}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Características pessoais">
          <CampoData
            rotulo="Data de nascimento"
            valor={filtros.nascimento}
            min="1900-01-02"
            aoMudar={(nascimento) => definir({ nascimento })}
          />
          <CampoLeitura rotulo="Idade atual" valor={dados ? `${dados.idade_atual} anos` : '—'} />
          {numero('patrimonio', 'Patrimônio inicial', {
            ...REAIS,
            step: 10000,
            w: LARGURA.texto,
          })}
        </GrupoFiltros>
        <GrupoFiltros titulo="Acumulação">
          {numero('acumulacao', 'Anos de acumulação', { max: 100, step: 1, w: LARGURA.data })}
          {numero('aporte', 'Aporte mensal', {
            ...REAIS,
            step: 1000,
            disabled: filtros.acumulacao === 0,
            w: LARGURA.texto,
          })}
        </GrupoFiltros>
        <GrupoFiltros titulo="Distribuição">
          {filtros.metodo === 'expectativa' ? (
            <>
              {numero('expectativa', 'Expectativa de vida', {
                min: dados?.expectativa_minima ?? 0,
                max: 120,
                step: 1,
                w: LARGURA.data,
              })}
              <CampoLeitura
                rotulo="Anos de distribuição"
                valor={dados ? `${dados.periodo_distribuicao.toFixed(0)} anos` : '—'}
              />
            </>
          ) : (
            <>
              {numero('distribuicao', 'Anos de distribuição', {
                min: 1,
                max: 100,
                step: 1,
                w: LARGURA.data,
              })}
              <CampoLeitura
                rotulo="Expectativa (calculada)"
                valor={dados ? `${dados.expectativa_vida.toFixed(0)} anos` : '—'}
              />
            </>
          )}
          {numero('resgate', 'Resgate mensal', { ...REAIS, step: 1000, w: LARGURA.texto })}
        </GrupoFiltros>
        <GrupoFiltros titulo="Hipóteses de mercado">
          {numero('rentabilidade', 'Rentabilidade nominal (a.a.)', {
            ...PERCENTUAL,
            max: 100,
            w: LARGURA.texto,
          })}
          {numero('inflacao', 'Inflação (a.a.)', { ...PERCENTUAL, max: 100, w: LARGURA.data })}
          {numero('aliquota', 'Alíquota de impostos', {
            ...PERCENTUAL,
            max: 100,
            w: LARGURA.data,
          })}
        </GrupoFiltros>
        {meta && (
          <GrupoFiltros titulo="Meta">
            {numero('alvo', 'Patrimônio final alvo', { ...REAIS, step: 50000, w: LARGURA.texto })}
            <Select
              label="Calcular"
              allowDeselect={false}
              data={(dados?.opcoes_meta ?? Object.keys(ROTULOS_META)).map((v) => ({
                value: v,
                label: ROTULOS_META[v] ?? v,
              }))}
              value={dados?.variavel ?? (filtros.variavel || null)}
              onChange={(variavel) => variavel && definir({ variavel })}
              w={LARGURA.selecao}
            />
          </GrupoFiltros>
        )}
      </BarraFiltros>
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível calcular a simulação.'}
        </Alert>
      )}
      {dados && (
        <>
          {dados.erros.map((e) => (
            <Alert key={e} color="red">
              {e}
            </Alert>
          ))}
          {meta && dados.meta && (
            <Secao
              titulo="Resultado da meta"
              legenda="Valor da variável escolhida que leva ao patrimônio final alvo."
            >
              {'erro' in dados.meta ? (
                <Alert color="red">{dados.meta.erro}</Alert>
              ) : (
                <Kpi kpi={dados.meta} />
              )}
            </Secao>
          )}
          <Secao
            titulo="Resumo do planejamento"
            legenda="Idades, fases e alertas do cenário simulado."
          >
            {dados.cartoes[0] && <Cartao itens={dados.cartoes[0]} />}
            <SimpleGrid cols={{ base: 1, md: 3 }}>
              {dados.cartoes.slice(1).map((c, i) => (
                <Cartao key={i} itens={c} />
              ))}
            </SimpleGrid>
            {dados.avisos.map((a) => (
              <Alert key={a.texto} color={a.tipo === 'info' ? 'blue' : 'yellow'}>
                {a.texto}
              </Alert>
            ))}
          </Secao>
          <Secao titulo="Resultados da simulação" legenda="Valores nominais ao fim do horizonte.">
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              {dados.kpis.map((k) => (
                <Kpi key={k.rotulo} kpi={k} />
              ))}
            </SimpleGrid>
          </Secao>
          <Secao
            titulo="Evolução do patrimônio"
            legenda="Patrimônio e valores acumulados por idade."
          >
            <Grafico grafico={dados.graficos.evolucao} />
          </Secao>
          <Secao
            titulo="Contribuições para o patrimônio"
            legenda="Do patrimônio inicial ao final: rendimentos, aportes, resgates e impostos."
          >
            <Grafico grafico={dados.graficos.contribuicoes} />
          </Secao>
          <Secao
            titulo="Memória de cálculo"
            legenda="Valores por ano de idade ou por mês da simulação."
          >
            <Tabs defaultValue="anual" keepMounted={false} color="dourado">
              <Tabs.List>
                <Tabs.Tab value="anual">Anual</Tabs.Tab>
                <Tabs.Tab value="mensal">Mensal</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="anual" pt="xs">
                <TabelaDados tabela={dados.tabelas.anual} altura={450} />
              </Tabs.Panel>
              <Tabs.Panel value="mensal" pt="xs">
                <TabelaDados tabela={dados.tabelas.mensal} altura={450} />
              </Tabs.Panel>
            </Tabs>
          </Secao>
        </>
      )}
    </Pagina>
  );
}
